import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Container, Row, Col, Card, Button, Badge, Modal, Image, Form, Alert, InputGroup, Tabs, Tab } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import {
    fetchComprasKanban,
    atualizarStatusCompra,
    excluirSolicitacaoCompra,
    criarPedidoLimeira,
    atualizarItemParaFornecedor,
    aprovarMultiplosCompra,
    excluirMultiplosSolicitacao
} from '../../services/api';
import Loading from '../../components/Loading/Loading';

const URGENCIA_CONFIG = {
    baixa: { label: 'Baixa', color: 'secondary', icon: '🟢' },
    normal: { label: 'Normal', color: 'info', icon: '🔵' },
    alta: { label: 'Alta', color: 'warning', icon: '🟡' },
    urgente: { label: 'Urgente', color: 'danger', icon: '🔴' }
};

const Limeira = () => {
    const queryClient = useQueryClient();
    const { isAdmin, user } = useAuth();

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [actionType, setActionType] = useState('');

    // Estado para modal de observação
    const [showObsModal, setShowObsModal] = useState(false);
    const [obsContent, setObsContent] = useState('');

    // Estados para criar pedido
    const [showCriarPedidoModal, setShowCriarPedidoModal] = useState(false);
    const [observacoesPedido, setObservacoesPedido] = useState('');
    const [alertPedido, setAlertPedido] = useState(null);

    // Estados para editar item
    const [showEditarItemModal, setShowEditarItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editQtd, setEditQtd] = useState(1);
    const [editUrgencia, setEditUrgencia] = useState('normal');
    const [editObsFornecedor, setEditObsFornecedor] = useState('');

    // Estados para filtros de busca
    const [filtroCodigo, setFiltroCodigo] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState('');

    // Estado para aba de loja selecionada (separação por origem)
    const [lojaAguardando, setLojaAguardando] = useState('ecommerce');
    const [lojaAprovados, setLojaAprovados] = useState('ecommerce');

    // Estado para modal de aprovar todos
    const [showAprovarTodosModal, setShowAprovarTodosModal] = useState(false);

    // Estados para seleção múltipla
    const [modoSelecao, setModoSelecao] = useState(null); // 'excluir' ou 'aprovar'
    const [itensSelecionados, setItensSelecionados] = useState(new Set());
    const [showExcluirMultiplosModal, setShowExcluirMultiplosModal] = useState(false);

    // Estado para feedback de cópia de código
    const [copiedCode, setCopiedCode] = useState(null);

    // Estados para tooltip de histórico de vendas
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [activeProduct, setActiveProduct] = useState(null);

    // Tooltip Handlers
    const handleMouseEnter = useCallback((event, produto) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const tooltipWidth = 180;
        const tooltipHeight = 200;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const gap = 8;

        let left = rect.right + gap;
        if (left + tooltipWidth > windowWidth - gap) {
            left = rect.left - tooltipWidth - gap;
        }
        if (left < gap) {
            left = gap;
        }

        let top = rect.top;
        if (top + tooltipHeight > windowHeight - gap) {
            top = windowHeight - tooltipHeight - gap;
        }
        if (top < gap) {
            top = gap;
        }

        setTooltipPosition({ top, left, maxHeight: windowHeight - gap * 2 });
        setActiveProduct(produto);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setActiveProduct(null);
    }, []);

    // Query Kanban
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['compras-kanban'],
        queryFn: () => fetchComprasKanban({}),
        refetchInterval: 30000
    });

    const kanbanData = data?.data || {};

    // Mutations
    const atualizarMutation = useMutation({
        mutationFn: ({ id, dados }) => atualizarStatusCompra(id, dados),
        onSuccess: () => {
            queryClient.invalidateQueries(['compras-kanban']);
            setShowConfirmModal(false);
            setSelectedCard(null);
        }
    });

    const excluirMutation = useMutation({
        mutationFn: excluirSolicitacaoCompra,
        onSuccess: () => {
            queryClient.invalidateQueries(['compras-kanban']);
            setShowConfirmModal(false);
            setSelectedCard(null);
        }
    });

    // Mutation para criar pedido
    const criarPedidoMutation = useMutation({
        mutationFn: criarPedidoLimeira,
        onSuccess: (data) => {
            queryClient.invalidateQueries(['compras-kanban']);
            setShowCriarPedidoModal(false);
            setObservacoesPedido('');
            setAlertPedido({ type: 'success', message: `Pedido ${data.data?.numero_pedido || ''} criado com sucesso!` });
            setTimeout(() => setAlertPedido(null), 5000);
        },
        onError: (error) => {
            setAlertPedido({ type: 'danger', message: error.message || 'Erro ao criar pedido' });
        }
    });

    // Mutation para atualizar item
    const atualizarItemMutation = useMutation({
        mutationFn: ({ id, dados }) => atualizarItemParaFornecedor(id, dados),
        onSuccess: () => {
            queryClient.invalidateQueries(['compras-kanban']);
            setShowEditarItemModal(false);
            setEditingItem(null);
        }
    });

    // Mutation para aprovar múltiplos itens
    const aprovarMultiplosMutation = useMutation({
        mutationFn: (ids) => aprovarMultiplosCompra(ids),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['compras-kanban']);
            setShowAprovarTodosModal(false);
            setAlertPedido({ type: 'success', message: data.message || 'Itens aprovados com sucesso!' });
            setTimeout(() => setAlertPedido(null), 5000);
        },
        onError: (error) => {
            setAlertPedido({ type: 'danger', message: error.message || 'Erro ao aprovar itens' });
        }
    });

    // Mutation para excluir múltiplos itens
    const excluirMultiplosMutation = useMutation({
        mutationFn: (ids) => excluirMultiplosSolicitacao(ids),
        onSuccess: (data) => {
            queryClient.invalidateQueries(['compras-kanban']);
            setShowExcluirMultiplosModal(false);
            setModoSelecao(null);
            setItensSelecionados(new Set());
            setAlertPedido({ type: 'success', message: `${data.sucesso} item(s) excluído(s) com sucesso!` });
            setTimeout(() => setAlertPedido(null), 5000);
        },
        onError: (error) => {
            setAlertPedido({ type: 'danger', message: error.message || 'Erro ao excluir itens' });
        }
    });

    // Handlers para seleção múltipla
    const toggleModoSelecao = (modo) => {
        if (modoSelecao === modo) {
            setModoSelecao(null);
            setItensSelecionados(new Set());
        } else {
            setModoSelecao(modo);
            setItensSelecionados(new Set());
        }
    };

    const toggleSelecionarItem = (id) => {
        setItensSelecionados(prev => {
            const novo = new Set(prev);
            if (novo.has(id)) {
                novo.delete(id);
            } else {
                novo.add(id);
            }
            return novo;
        });
    };

    const selecionarTodosVisiveis = () => {
        const ids = aguardandoAprovacaoFiltrado.map(item => item.id);
        setItensSelecionados(new Set(ids));
    };

    const deselecionarTodos = () => {
        setItensSelecionados(new Set());
    };

    const confirmarExcluirSelecionados = () => {
        const ids = Array.from(itensSelecionados);
        if (ids.length > 0) {
            excluirMultiplosMutation.mutate(ids);
        }
    };

    const confirmarAprovarSelecionados = () => {
        const ids = Array.from(itensSelecionados);
        if (ids.length > 0) {
            aprovarMultiplosMutation.mutate(ids);
        }
    };

    // Função auxiliar para normalizar origem da loja
    const normalizarOrigem = (origem) => {
        if (!origem) return 'ecommerce';
        const o = origem.toLowerCase();
        if (o.includes('centro')) return 'centro';
        if (o.includes('caxias')) return 'caxias';
        return 'ecommerce';
    };

    // Separa itens por loja
    const separarPorLoja = (itens) => {
        const resultado = { ecommerce: [], centro: [], caxias: [] };
        (itens || []).forEach(item => {
            const loja = normalizarOrigem(item.origem);
            resultado[loja].push(item);
        });
        return resultado;
    };

    // Itens aguardando separados por loja
    const aguardandoPorLoja = useMemo(() => {
        return separarPorLoja(kanbanData.solicitado);
    }, [kanbanData]);

    // Itens aprovados separados por loja
    const aprovadosPorLoja = useMemo(() => {
        return separarPorLoja(kanbanData.aprovado);
    }, [kanbanData]);

    // Contadores por loja
    const contadoresAguardando = useMemo(() => ({
        ecommerce: aguardandoPorLoja.ecommerce.length,
        centro: aguardandoPorLoja.centro.length,
        caxias: aguardandoPorLoja.caxias.length
    }), [aguardandoPorLoja]);

    const contadoresAprovados = useMemo(() => ({
        ecommerce: aprovadosPorLoja.ecommerce.length,
        centro: aprovadosPorLoja.centro.length,
        caxias: aprovadosPorLoja.caxias.length
    }), [aprovadosPorLoja]);

    // Extrair categorias únicas dos itens aguardando aprovação (da loja selecionada)
    const categoriasDisponiveis = useMemo(() => {
        const aguardando = aguardandoPorLoja[lojaAguardando] || [];
        const categorias = new Set();
        aguardando.forEach(item => {
            // Tentar determinar categoria pelo fornecedor ou descrição
            if (item.fornecedor) {
                categorias.add(item.fornecedor);
            }
        });
        return Array.from(categorias).sort();
    }, [aguardandoPorLoja, lojaAguardando]);

    // Filtrar itens aguardando aprovação (da loja selecionada)
    const aguardandoAprovacaoFiltrado = useMemo(() => {
        let itens = aguardandoPorLoja[lojaAguardando] || [];

        // Filtrar por código
        if (filtroCodigo.trim()) {
            const termo = filtroCodigo.toLowerCase().trim();
            itens = itens.filter(item =>
                item.produto_codigo?.toLowerCase().includes(termo) ||
                item.produto_descricao?.toLowerCase().includes(termo)
            );
        }

        // Filtrar por categoria (fornecedor)
        if (filtroCategoria) {
            itens = itens.filter(item => item.fornecedor === filtroCategoria);
        }

        return itens;
    }, [aguardandoPorLoja, lojaAguardando, filtroCodigo, filtroCategoria]);

    // Handler para aprovar todos (filtrados ou todos)
    const handleAprovarTodos = () => {
        setShowAprovarTodosModal(true);
    };

    const confirmarAprovarTodos = () => {
        const ids = aguardandoAprovacaoFiltrado.map(item => item.id);
        if (ids.length > 0) {
            aprovarMultiplosMutation.mutate(ids);
        }
    };

    const formatCurrency = (value) => {
        if (!value) return 'R$ -';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    // Copia o código do produto para a área de transferência
    const handleCopiarCodigo = (e, codigo) => {
        e.preventDefault();
        e.stopPropagation();
        if (codigo) {
            navigator.clipboard.writeText(codigo);
            setCopiedCode(codigo);
            setTimeout(() => setCopiedCode(null), 2000);
        }
    };

    const handleAprovar = (item) => {
        // Aprovação direta sem modal de confirmação
        atualizarMutation.mutate({
            id: item.id,
            dados: {
                status: 'aprovado',
                observacoes: `Aprovado por ${user?.nome || 'Admin'} em ${new Date().toLocaleDateString('pt-BR')}`
            }
        });
    };

    const handleExcluir = (item) => {
        setSelectedCard(item);
        setActionType('excluir');
        setShowConfirmModal(true);
    };

    // Handler para reverter aprovação (voltar para solicitado)
    const handleReverterAprovacao = (item) => {
        setSelectedCard(item);
        setActionType('reverter');
        setShowConfirmModal(true);
    };

    const handleVerObservacao = (obs) => {
        setObsContent(obs);
        setShowObsModal(true);
    };

    // Handler para editar item (quantidade e obs para fornecedor)
    const handleEditarItem = (item) => {
        setEditingItem(item);
        setEditQtd(item.quantidade_solicitada || 1);
        setEditUrgencia(item.urgencia || 'normal');
        setEditObsFornecedor(item.observacoes_fornecedor || '');
        setShowEditarItemModal(true);
    };

    const confirmarEdicaoItem = () => {
        if (editingItem) {
            atualizarItemMutation.mutate({
                id: editingItem.id,
                dados: {
                    quantidade_solicitada: editQtd,
                    urgencia: editUrgencia,
                    observacoes_fornecedor: editObsFornecedor
                }
            });
        }
    };

    // Handler para criar pedido
    const handleCriarPedido = () => {
        setShowCriarPedidoModal(true);
    };

    const confirmarCriarPedido = () => {
        const aprovadosSemPedido = aprovados.filter(item => !item.pedido_id);
        if (aprovadosSemPedido.length === 0) {
            setAlertPedido({ type: 'warning', message: 'Não há itens aprovados disponíveis para criar pedido' });
            return;
        }
        criarPedidoMutation.mutate({
            item_ids: aprovadosSemPedido.map(item => item.id),
            observacoes_gerais: observacoesPedido
        });
    };

    const confirmAction = () => {
        if (actionType === 'aprovar') {
            atualizarMutation.mutate({
                id: selectedCard.id,
                dados: {
                    status: 'aprovado',
                    observacoes: `Aprovado por ${user?.nome || 'Admin'} em ${new Date().toLocaleDateString('pt-BR')}`
                }
            });
        } else if (actionType === 'excluir') {
            excluirMutation.mutate(selectedCard.id);
        } else if (actionType === 'reverter') {
            atualizarMutation.mutate({
                id: selectedCard.id,
                dados: {
                    status: 'solicitado',
                    observacoes: `Aprovação revertida por ${user?.nome || 'Admin'} em ${new Date().toLocaleDateString('pt-BR')}`
                }
            });
        }
    };

    // --- Drag and Drop Logic ---
    const handleDragStart = (e, item) => {
        if (!isAdmin()) return; // Apenas admin arrasta
        e.dataTransfer.setData('text/plain', JSON.stringify(item));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        if (!isAdmin()) return;
        e.preventDefault(); // Necessário para permitir o drop
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDropAprovar = (e) => {
        if (!isAdmin()) return;
        e.preventDefault();
        const data = e.dataTransfer.getData('text/plain');
        if (data) {
            try {
                const item = JSON.parse(data);
                if (item && item.status !== 'aprovado') {
                    // Trigger aprovação direta via drop
                    atualizarMutation.mutate({
                        id: item.id,
                        dados: {
                            status: 'aprovado',
                            observacoes: `Aprovado por arrasto por ${user?.nome || 'Admin'} em ${new Date().toLocaleDateString('pt-BR')}`
                        }
                    });
                }
            } catch (e) {
                console.error("Erro ao processar drop:", e);
            }
        }
    };

    // Aprovados da loja selecionada
    const aprovados = aprovadosPorLoja[lojaAprovados] || [];

    // Total geral de aprovados (para stats)
    const totalAprovados = (kanbanData.aprovado || []).length;
    const totalAguardando = (kanbanData.solicitado || []).length;

    // Card estilo catálogo de produtos (Estilo E-commerce)
    const renderProductCard = (item, isAprovado = false) => {
        const estaSelecionado = itensSelecionados.has(item.id);
        const emModoSelecao = modoSelecao !== null && !isAprovado;

        return (
            <Card
                key={item.id}
                className={`h-100 shadow-sm border-0 ${!isAprovado && isAdmin() && !modoSelecao ? 'cursor-grab' : ''} ${estaSelecionado ? 'border-3 border-primary' : ''}`}
                draggable={!isAprovado && isAdmin() && !modoSelecao}
                onDragStart={(e) => handleDragStart(e, item)}
                onClick={emModoSelecao ? () => toggleSelecionarItem(item.id) : undefined}
                onMouseEnter={(e) => handleMouseEnter(e, item)}
                onMouseLeave={handleMouseLeave}
                style={{
                    cursor: emModoSelecao ? 'pointer' : (!isAprovado && isAdmin() ? 'grab' : 'default'),
                    fontSize: '0.85rem',
                    opacity: emModoSelecao && !estaSelecionado ? 0.7 : 1,
                    transition: 'all 0.2s'
                }}
            >
                {/* Checkbox de seleção */}
                {emModoSelecao && (
                    <div style={{ position: 'absolute', top: '5px', right: '5px', zIndex: 10 }}>
                        <Form.Check
                            type="checkbox"
                            checked={estaSelecionado}
                            onChange={() => toggleSelecionarItem(item.id)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ transform: 'scale(1.3)' }}
                        />
                    </div>
                )}
                {/* Imagem do produto */}
                <div style={{ position: 'relative', paddingTop: '100%', background: '#f8f9fa' }}>
                    {item.produto_imagem ? (
                        <img
                            src={item.produto_imagem}
                            loading="lazy"
                            alt={item.produto_descricao || 'Produto'}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    ) : null}
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: item.produto_imagem ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#cbd5e1',
                            fontSize: '2rem'
                        }}
                    >
                        📷
                    </div>

                    {/* Badge Qtd */}
                    <Badge
                        bg="dark"
                        style={{ position: 'absolute', top: '5px', left: '5px', fontSize: '0.7rem', opacity: 0.9 }}
                    >
                        {item.quantidade_solicitada} un
                    </Badge>

                    {/* Badge Urgencia (apenas se nao for normal) */}
                    {item.urgencia !== 'normal' && (
                        <Badge
                            bg={URGENCIA_CONFIG[item.urgencia]?.color || 'secondary'}
                            style={{ position: 'absolute', top: '5px', right: '5px', fontSize: '0.65rem' }}
                            title={URGENCIA_CONFIG[item.urgencia]?.label}
                        >
                            {URGENCIA_CONFIG[item.urgencia]?.icon}
                        </Badge>
                    )}

                    {/* Indicador de Obs */}
                    {item.observacoes && (
                        <div style={{ position: 'absolute', bottom: '5px', right: '5px', fontSize: '0.8rem' }} title="Possui observação">
                            📝
                        </div>
                    )}
                </div>

                {/* Corpo do card */}
                <Card.Body className="p-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <span
                            style={{
                                fontSize: '0.72rem',
                                color: copiedCode === item.produto_codigo ? '#fff' : '#4e73df',
                                cursor: 'pointer',
                                fontWeight: 700,
                                transition: 'all 0.25s',
                                userSelect: 'text',
                                background: copiedCode === item.produto_codigo
                                    ? 'linear-gradient(135deg, #1cc88a, #17a673)'
                                    : 'rgba(78, 115, 223, 0.1)',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                border: copiedCode === item.produto_codigo
                                    ? '1px solid #1cc88a'
                                    : '1px solid rgba(78, 115, 223, 0.25)',
                                letterSpacing: '0.3px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                whiteSpace: 'nowrap'
                            }}
                            onClick={(e) => handleCopiarCodigo(e, item.produto_codigo)}
                            title="Clique para copiar código"
                            onMouseEnter={(e) => {
                                if (copiedCode !== item.produto_codigo) {
                                    e.currentTarget.style.background = 'rgba(78, 115, 223, 0.2)';
                                    e.currentTarget.style.borderColor = '#4e73df';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (copiedCode !== item.produto_codigo) {
                                    e.currentTarget.style.background = 'rgba(78, 115, 223, 0.1)';
                                    e.currentTarget.style.borderColor = 'rgba(78, 115, 223, 0.25)';
                                }
                            }}
                        >
                            {copiedCode === item.produto_codigo ? '✅' : '📋'}
                            #{item.produto_codigo}
                            {copiedCode === item.produto_codigo && <span style={{ fontSize: '0.6rem', marginLeft: '2px' }}>Copiado!</span>}
                        </span>
                        <Badge bg="light" text="dark" className="border" style={{ fontSize: '0.6rem', fontWeight: 'normal' }}>
                            {item.origem === 'Loja Centro' ? 'Centro' : item.origem === 'Loja Caxias' ? 'Caxias' : 'Ecom'}
                        </Badge>
                    </div>

                    <div className="fw-bold text-truncate mb-1" title={item.produto_descricao} style={{ fontSize: '0.8rem', color: '#1e293b' }}>
                        {item.produto_descricao}
                    </div>

                    <div className="d-flex justify-content-between align-items-end mt-2">
                        <div className="text-truncate" style={{ maxWidth: '60%' }}>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{item.fornecedor?.split(' ')[0] || 'N/A'}</div>
                            <div className="text-secondary fw-bold" style={{ fontSize: '0.75rem' }}>Unit: {formatCurrency(item.preco_custo)}</div>
                        </div>
                        <div className="text-success fw-bold" style={{ fontSize: '0.85rem' }}>
                            {formatCurrency(item.valor_total)}
                        </div>
                    </div>
                </Card.Body>

                {/* Footer compact com ações */}
                {isAdmin() && (
                    <Card.Footer className="p-1 bg-white border-top-0">
                        {!isAprovado ? (
                            <div className="d-flex gap-1">
                                <Button
                                    variant="success"
                                    size="sm"
                                    className="flex-grow-1 py-1"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={() => handleAprovar(item)}
                                    title="Aprovar Solicitação"
                                >
                                    ✅
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="py-1"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={() => handleEditarItem(item)}
                                    title="Editar"
                                >
                                    ✏️
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="py-1"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={() => handleExcluir(item)}
                                    title="Excluir"
                                >
                                    🗑️
                                </Button>
                            </div>
                        ) : (
                            <div className="d-flex justify-content-between align-items-center py-1 gap-1">
                                <Badge bg="success" className="bg-opacity-10 text-success border border-success flex-grow-1 text-center">
                                    ✅ Aprovado
                                </Badge>
                                <Button
                                    variant="outline-warning"
                                    size="sm"
                                    className="py-1"
                                    style={{ fontSize: '0.7rem' }}
                                    onClick={() => handleReverterAprovacao(item)}
                                    title="Reverter aprovação (voltar para aguardando)"
                                >
                                    ↩️
                                </Button>
                            </div>
                        )}
                    </Card.Footer>
                )}
            </Card>
        );
    };

    return (
        <Container fluid className="p-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 mb-0 text-gray-800">📋 Solicitações de Compra</h1>
                    <p className="mb-0 text-muted">Aprovação de pedidos de compra para a Limeira</p>
                </div>
                <Button variant="outline-secondary" onClick={() => refetch()}>
                    🔄 Atualizar
                </Button>
            </div>

            {/* Alert de Pedido */}
            {alertPedido && (
                <Alert variant={alertPedido.type} dismissible onClose={() => setAlertPedido(null)} className="mb-4">
                    {alertPedido.message}
                </Alert>
            )}

            {/* Stats por Loja */}
            <Row className="mb-4 g-3">
                <Col md={6}>
                    <Card className="border-warning border-2">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <div className="rounded-circle bg-warning text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>⏳</div>
                                <div>
                                    <h5 className="mb-0">{totalAguardando}</h5>
                                    <small className="text-muted">Aguardando Aprovação</small>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                <Badge bg="primary" className="d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                                    🛒 {contadoresAguardando.ecommerce}
                                </Badge>
                                <Badge bg="info" className="d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                                    🏪 {contadoresAguardando.centro}
                                </Badge>
                                <Badge bg="secondary" className="d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                                    📍 {contadoresAguardando.caxias}
                                </Badge>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="border-success border-2">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <div className="rounded-circle bg-success text-white p-3 me-3" style={{ fontSize: '1.5rem' }}>✅</div>
                                <div>
                                    <h5 className="mb-0">{totalAprovados}</h5>
                                    <small className="text-muted">Aprovados</small>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                <Badge bg="primary" className="d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                                    🛒 {contadoresAprovados.ecommerce}
                                </Badge>
                                <Badge bg="info" className="d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                                    🏪 {contadoresAprovados.centro}
                                </Badge>
                                <Badge bg="secondary" className="d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                                    📍 {contadoresAprovados.caxias}
                                </Badge>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Loading */}
            {isLoading && <Loading />}

            {/* Kanban com Abas por Loja */}
            {!isLoading && (
                <Row className="g-4">
                    {/* Coluna Aguardando Aprovação */}
                    <Col lg={6}>
                        <Card className="h-100 bg-light border-warning">
                            <Card.Header className="bg-warning text-dark p-2">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>⏳ Aguardando Aprovação</span>
                                    <div className="d-flex gap-2 align-items-center flex-wrap">
                                        <Badge bg="dark">{aguardandoAprovacaoFiltrado.length}</Badge>
                                        {isAdmin() && aguardandoAprovacaoFiltrado.length > 0 && !modoSelecao && (
                                            <>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => toggleModoSelecao('excluir')}
                                                    title="Selecionar itens para excluir"
                                                >
                                                    🗑️ Excluir
                                                </Button>
                                                <Button
                                                    variant="dark"
                                                    size="sm"
                                                    onClick={() => toggleModoSelecao('aprovar')}
                                                    title="Selecionar itens para aprovar"
                                                >
                                                    ✅ Aprovar
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Barra de ações quando em modo de seleção */}
                                {modoSelecao && (
                                    <Alert
                                        variant={modoSelecao === 'excluir' ? 'danger' : 'success'}
                                        className="py-2 mb-2 d-flex justify-content-between align-items-center"
                                    >
                                        <div>
                                            <strong>{modoSelecao === 'excluir' ? '🗑️ Modo Exclusão' : '✅ Modo Aprovação'}:</strong>
                                            {' '}{itensSelecionados.size} item(s) selecionado(s)
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-dark" size="sm" onClick={selecionarTodosVisiveis}>
                                                Selecionar Todos
                                            </Button>
                                            <Button variant="outline-dark" size="sm" onClick={deselecionarTodos}>
                                                Limpar
                                            </Button>
                                            <Button
                                                variant={modoSelecao === 'excluir' ? 'danger' : 'success'}
                                                size="sm"
                                                disabled={itensSelecionados.size === 0}
                                                onClick={() => {
                                                    if (modoSelecao === 'excluir') {
                                                        setShowExcluirMultiplosModal(true);
                                                    } else {
                                                        confirmarAprovarSelecionados();
                                                    }
                                                }}
                                            >
                                                {modoSelecao === 'excluir' ? `Excluir (${itensSelecionados.size})` : `Aprovar (${itensSelecionados.size})`}
                                            </Button>
                                            <Button variant="secondary" size="sm" onClick={() => toggleModoSelecao(null)}>
                                                Cancelar
                                            </Button>
                                        </div>
                                    </Alert>
                                )}

                                {/* Abas de Loja */}
                                <Tabs
                                    activeKey={lojaAguardando}
                                    onSelect={(k) => { setLojaAguardando(k); setFiltroCategoria(''); }}
                                    className="mb-2 tabs-loja-branco"
                                    variant="pills"
                                    fill
                                >
                                    <Tab
                                        eventKey="ecommerce"
                                        title={<span style={{ color: 'white' }}>🛒 E-commerce <Badge bg="dark">{contadoresAguardando.ecommerce}</Badge></span>}
                                    />
                                    <Tab
                                        eventKey="centro"
                                        title={<span style={{ color: 'white' }}>🏪 Centro <Badge bg="dark">{contadoresAguardando.centro}</Badge></span>}
                                    />
                                    <Tab
                                        eventKey="caxias"
                                        title={<span style={{ color: 'white' }}>📍 Caxias <Badge bg="dark">{contadoresAguardando.caxias}</Badge></span>}
                                    />
                                </Tabs>

                                {/* Filtros */}
                                <Row className="g-2">
                                    <Col xs={6}>
                                        <InputGroup size="sm">
                                            <InputGroup.Text>🔍</InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Código ou descrição..."
                                                value={filtroCodigo}
                                                onChange={(e) => setFiltroCodigo(e.target.value)}
                                            />
                                            {filtroCodigo && (
                                                <Button variant="outline-dark" onClick={() => setFiltroCodigo('')}>✕</Button>
                                            )}
                                        </InputGroup>
                                    </Col>
                                    <Col xs={6}>
                                        <Form.Select
                                            size="sm"
                                            value={filtroCategoria}
                                            onChange={(e) => setFiltroCategoria(e.target.value)}
                                        >
                                            <option value="">Todos Fornecedores</option>
                                            {categoriasDisponiveis.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>
                            </Card.Header>
                            <Card.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                                {aguardandoAprovacaoFiltrado.length === 0 ? (
                                    <div className="text-center text-muted py-5">
                                        <p className="mb-0">
                                            {(filtroCodigo || filtroCategoria)
                                                ? 'Nenhum item encontrado com os filtros aplicados'
                                                : `Nenhuma solicitação pendente para ${lojaAguardando === 'ecommerce' ? 'E-commerce' : lojaAguardando === 'centro' ? 'Loja Centro' : 'Loja Caxias'}`}
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(195px, 1fr))', gap: '0.75rem' }}>
                                        {aguardandoAprovacaoFiltrado.map(item => (
                                            <div key={item.id}>
                                                {renderProductCard(item, false)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Coluna Aprovados (Droppable) */}
                    <Col lg={6}>
                        <Card
                            className="h-100 border-success"
                            onDragOver={handleDragOver}
                            onDrop={handleDropAprovar}
                            style={{ transition: 'background-color 0.2s' }}
                        >
                            <Card.Header className="bg-success text-white p-2">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>✅ Aprovados (Arraste aqui para aprovar)</span>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Badge bg="light" text="dark">{aprovados.length}</Badge>
                                        {isAdmin() && aprovados.filter(i => !i.pedido_id).length > 0 && (
                                            <Button
                                                variant="light"
                                                size="sm"
                                                onClick={handleCriarPedido}
                                                title="Criar pedido com todos os itens aprovados"
                                            >
                                                📦 Criar Pedido ({aprovados.filter(i => !i.pedido_id).length})
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Abas de Loja */}
                                <Tabs
                                    activeKey={lojaAprovados}
                                    onSelect={(k) => setLojaAprovados(k)}
                                    className="mb-0 tabs-loja-branco"
                                    variant="pills"
                                    fill
                                >
                                    <Tab
                                        eventKey="ecommerce"
                                        title={<span style={{ color: 'white' }}>🛒 E-commerce <Badge bg="dark">{contadoresAprovados.ecommerce}</Badge></span>}
                                    />
                                    <Tab
                                        eventKey="centro"
                                        title={<span style={{ color: 'white' }}>🏪 Centro <Badge bg="dark">{contadoresAprovados.centro}</Badge></span>}
                                    />
                                    <Tab
                                        eventKey="caxias"
                                        title={<span style={{ color: 'white' }}>📍 Caxias <Badge bg="dark">{contadoresAprovados.caxias}</Badge></span>}
                                    />
                                </Tabs>
                            </Card.Header>
                            <Card.Body style={{ maxHeight: '65vh', overflowY: 'auto', background: '#e8f5e9' }}>
                                {aprovados.length === 0 ? (
                                    <div className="text-center text-muted py-5">
                                        <p className="mb-0">
                                            {`Nenhum item aprovado para ${lojaAprovados === 'ecommerce' ? 'E-commerce' : lojaAprovados === 'centro' ? 'Loja Centro' : 'Loja Caxias'}`}
                                        </p>
                                        <small>Arraste itens aqui para aprovar</small>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(195px, 1fr))', gap: '0.75rem' }}>
                                        {aprovados.map(item => (
                                            <div key={item.id}>
                                                {renderProductCard(item, true)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Modal de Confirmação */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {actionType === 'aprovar' && '✅ Confirmar Aprovação'}
                        {actionType === 'excluir' && '🗑️ Confirmar Exclusão'}
                        {actionType === 'reverter' && '↩️ Reverter Aprovação'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCard && (
                        <div className="text-center">
                            {selectedCard.produto_imagem && (
                                <Image
                                    src={selectedCard.produto_imagem}
                                    style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'contain' }}
                                    className="mb-3"
                                />
                            )}
                            <p className="mb-2">
                                {actionType === 'aprovar' && 'Deseja aprovar esta solicitação?'}
                                {actionType === 'excluir' && 'Deseja excluir esta solicitação?'}
                                {actionType === 'reverter' && 'Deseja reverter a aprovação e voltar para "Aguardando"?'}
                            </p>
                            <div className="bg-light p-3 rounded text-start">
                                <strong>{selectedCard.produto_descricao}</strong>
                                <div className="small text-muted">
                                    <div>Fornecedor: {selectedCard.fornecedor}</div>
                                    <div>Custo: {formatCurrency(selectedCard.preco_custo)}</div>
                                    {selectedCard.observacoes && (
                                        <div className="mt-2 text-dark">Obs: {selectedCard.observacoes}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant={actionType === 'aprovar' ? 'success' : actionType === 'reverter' ? 'warning' : 'danger'}
                        onClick={confirmAction}
                        disabled={atualizarMutation.isPending || excluirMutation.isPending}
                    >
                        {atualizarMutation.isPending || excluirMutation.isPending
                            ? 'Processando...'
                            : actionType === 'aprovar' ? 'Aprovar' : actionType === 'reverter' ? 'Reverter' : 'Excluir'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Observação */}
            <Modal show={showObsModal} onHide={() => setShowObsModal(false)} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title className="h5">📝 Observação</Modal.Title>
                </Modal.Header>
                <Modal.Body >
                    <p className="mb-0">{obsContent || 'Nenhuma observação.'}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowObsModal(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal para Criar Pedido */}
            <Modal show={showCriarPedidoModal} onHide={() => setShowCriarPedidoModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>📦 Criar Pedido</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="info" className="py-2">
                        <small>
                            Será criado um pedido com <strong>{aprovados.filter(i => !i.pedido_id).length}</strong> itens aprovados.
                        </small>
                    </Alert>

                    <Form.Group className="mb-3">
                        <Form.Label>Observações gerais para o pedido (opcional):</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Ex: Entregar até dia 20/12, prazo de pagamento 30 dias..."
                            value={observacoesPedido}
                            onChange={(e) => setObservacoesPedido(e.target.value)}
                        />
                    </Form.Group>

                    {alertPedido && (
                        <Alert variant={alertPedido.type} className="py-2">
                            {alertPedido.message}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCriarPedidoModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="success"
                        onClick={confirmarCriarPedido}
                        disabled={criarPedidoMutation.isPending}
                    >
                        {criarPedidoMutation.isPending ? 'Criando...' : '📦 Criar Pedido'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal para Editar Item */}
            <Modal show={showEditarItemModal} onHide={() => setShowEditarItemModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>✏️ Editar Item</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingItem && (
                        <>
                            <div className="d-flex align-items-center mb-3">
                                {editingItem.produto_imagem && (
                                    <Image
                                        src={editingItem.produto_imagem}
                                        style={{ width: 60, height: 60, objectFit: 'cover' }}
                                        className="rounded me-3"
                                    />
                                )}
                                <div>
                                    <small className="text-muted">#{editingItem.produto_codigo}</small>
                                    <div className="fw-bold">{editingItem.produto_descricao}</div>
                                </div>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Quantidade:</Form.Label>
                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        style={{ minWidth: '52px', fontWeight: 'bold', fontSize: '1rem' }}
                                        onClick={() => setEditQtd(q => Math.max(1, (parseInt(q) || 1) - 10))}
                                    >−10</button>
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        value={editQtd}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            // Permite campo vazio durante a digitação
                                            if (v === '' || v === '-') {
                                                setEditQtd(v);
                                            } else {
                                                const n = parseInt(v);
                                                setEditQtd(isNaN(n) ? 1 : Math.max(1, n));
                                            }
                                        }}
                                        onBlur={() => {
                                            // Garante mínimo 1 ao sair do campo
                                            const n = parseInt(editQtd);
                                            setEditQtd(isNaN(n) || n < 1 ? 1 : n);
                                        }}
                                        style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem' }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        style={{ minWidth: '52px', fontWeight: 'bold', fontSize: '1rem' }}
                                        onClick={() => setEditQtd(q => (parseInt(q) || 1) + 10)}
                                    >+10</button>
                                </div>
                                <Form.Text className="text-muted">
                                    Use os botões para incrementar/decrementar de 10 em 10, ou digite qualquer valor.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Urgência:</Form.Label>
                                <Form.Select
                                    value={editUrgencia}
                                    onChange={(e) => setEditUrgencia(e.target.value)}
                                >
                                    {Object.entries(URGENCIA_CONFIG).map(([key, config]) => (
                                        <option key={key} value={key}>
                                            {config.icon} {config.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Observações para o Fornecedor:</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Ex: Preferir cor azul, embalagem reforçada..."
                                    value={editObsFornecedor}
                                    onChange={(e) => setEditObsFornecedor(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditarItemModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={confirmarEdicaoItem}
                        disabled={atualizarItemMutation.isPending}
                    >
                        {atualizarItemMutation.isPending ? 'Salvando...' : '💾 Salvar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal para Aprovar Todos */}
            <Modal show={showAprovarTodosModal} onHide={() => setShowAprovarTodosModal(false)} centered>
                <Modal.Header closeButton className="bg-success text-white">
                    <Modal.Title>✅ Aprovar Todos os Itens</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="py-2">
                        <strong>Atenção!</strong> Esta ação irá aprovar <strong>{aguardandoAprovacaoFiltrado.length}</strong> itens de uma vez.
                    </Alert>

                    {(filtroCodigo || filtroCategoria) && (
                        <Alert variant="info" className="py-2">
                            <small>
                                Filtros aplicados:
                                {filtroCodigo && <span className="ms-2">🔍 "{filtroCodigo}"</span>}
                                {filtroCategoria && <span className="ms-2">📁 {filtroCategoria}</span>}
                            </small>
                        </Alert>
                    )}

                    <div className="text-center py-3">
                        <div className="display-4 text-success mb-2">✅</div>
                        <h5>Confirmar aprovação?</h5>
                        <p className="text-muted mb-0">
                            Todos os <strong>{aguardandoAprovacaoFiltrado.length}</strong> itens
                            {filtroCategoria ? ` de "${filtroCategoria}"` : ''} serão aprovados.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAprovarTodosModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="success"
                        onClick={confirmarAprovarTodos}
                        disabled={aprovarMultiplosMutation.isPending}
                    >
                        {aprovarMultiplosMutation.isPending
                            ? 'Aprovando...'
                            : `✅ Aprovar ${aguardandoAprovacaoFiltrado.length} Itens`}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Confirmação para Exclusão Múltipla */}
            <Modal show={showExcluirMultiplosModal} onHide={() => setShowExcluirMultiplosModal(false)} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>🗑️ Confirmar Exclusão em Lote</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        <strong>Atenção!</strong> Você está prestes a excluir <strong>{itensSelecionados.size}</strong> solicitação(ões).
                        <br /><br />
                        Esta ação não pode ser desfeita.
                    </Alert>
                    <p className="text-muted mb-0">
                        Os seguintes itens serão excluídos permanentemente.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowExcluirMultiplosModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmarExcluirSelecionados}
                        disabled={excluirMultiplosMutation.isPending}
                    >
                        {excluirMultiplosMutation.isPending
                            ? 'Excluindo...'
                            : `🗑️ Excluir ${itensSelecionados.size} Itens`}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Tooltip Simples - Histórico de Vendas */}
            {activeProduct && (
                <div
                    style={{
                        position: 'fixed',
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                        background: '#1e293b',
                        color: '#f1f5f9',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        zIndex: 9999,
                        minWidth: '180px',
                        fontSize: '12px',
                        pointerEvents: 'none'
                    }}
                >
                    <div style={{ fontWeight: 600, marginBottom: '8px', color: '#94a3b8', fontSize: '11px' }}>
                        📊 Histórico de Vendas
                    </div>
                    {activeProduct.vendas_mensais && activeProduct.vendas_mensais.length > 0 ? (
                        activeProduct.vendas_mensais.slice(0, 6).map((venda, index) => {
                            const mesesAbrev = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
                            let mesFormatado = venda.mes;

                            if (typeof venda.mes === 'number' || /^\d+$/.test(venda.mes)) {
                                const mesNum = parseInt(venda.mes) - 1;
                                if (mesNum >= 0 && mesNum < 12) {
                                    mesFormatado = mesesAbrev[mesNum];
                                }
                            } else if (/^\d{4}-\d{2}/.test(venda.mes)) {
                                const mesNum = parseInt(venda.mes.split('-')[1]) - 1;
                                if (mesNum >= 0 && mesNum < 12) {
                                    mesFormatado = mesesAbrev[mesNum];
                                }
                            } else if (typeof venda.mes === 'string' && venda.mes.length > 3) {
                                mesFormatado = venda.mes.substring(0, 3).toLowerCase();
                            } else if (typeof venda.mes === 'string') {
                                mesFormatado = venda.mes.toLowerCase();
                            }

                            return (
                                <div
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '4px 0',
                                        borderBottom: index < 5 ? '1px solid #334155' : 'none'
                                    }}
                                >
                                    <span style={{ color: '#94a3b8' }}>{mesFormatado}</span>
                                    <span style={{ fontWeight: 600 }}>{venda.quantidade}</span>
                                </div>
                            );
                        })
                    ) : (
                        <div style={{ color: '#64748b', fontStyle: 'italic' }}>
                            Sem dados de vendas
                        </div>
                    )}
                </div>
            )}
        </Container>
    );
};

export default Limeira;

